import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, DatePicker, Descriptions, Empty, Form,
  Input, InputNumber, Modal, Row, Select, Space, Statistic, Table, Tabs,
  Tag, Tooltip, Typography, message
} from 'antd';
import {
  ReloadOutlined, ArrowLeftOutlined, PlusOutlined, LinkOutlined,
  DisconnectOutlined, DeleteOutlined, FileSearchOutlined,
  LockOutlined, StopOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import { ErpMasterShell } from '../../../../components/common/erp-grid';
import useApprovalMfa from '../../../../hooks/useApprovalMfa';
import { bankReconciliationApi } from './bankReconciliationApi';
import {
  RECONCILIATION_STATUS_COLOR, RECONCILIATION_STATUS_LABEL,
  MATCH_STATUS_COLOR, MATCH_STATUS_LABEL,
  formatBRSAmount, classifyDebitCredit,
  buildStatementLinePayload, findStatementLineFormErrors
} from './bankReconciliationHelpers';
import {
  friendlyAccountsErrorMessage, friendlyAccountsEmptyMessage
} from '../accountsPageState';
import '../accounts.css';

const { Title, Text } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Bank Reconciliation detail page (Stage 6E.1).
// Split-pane layout: bank statement lines on the left, book entries
// on the right. Click a line on each side and press Match.
// ─────────────────────────────────────────────────────────────────

function AddStatementLineModal({ open, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const save = useCallback(async () => {
    let values;
    try { values = await form.validateFields(); }
    catch (_e) { return; }
    const payload = buildStatementLinePayload({
      statementDate: values.statementDate?.format('YYYY-MM-DD'),
      valueDate:     values.valueDate?.format('YYYY-MM-DD') || null,
      description:   values.description || null,
      referenceNo:   values.referenceNo || null,
      chequeNo:      values.chequeNo || null,
      debitAmount:   values.debitAmount || 0,
      creditAmount:  values.creditAmount || 0,
      balanceAfter:  values.balanceAfter ?? null,
      remarks:       values.remarks || null
    });
    const errs = findStatementLineFormErrors(payload);
    if (errs.length) { message.error(errs[0][1]); return; }
    setSaving(true);
    try {
      await onSaved(payload);
      form.resetFields();
    } catch (e) {
      message.error(friendlyAccountsErrorMessage(e, 'bank_reconciliation_lines'));
    } finally { setSaving(false); }
  }, [form, onSaved]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={save}
      okText="Add line"
      confirmLoading={saving}
      title="Add bank statement line"
      width={620}
      destroyOnClose>
      <Form form={form} layout="vertical" preserve={false}>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item label="Statement date" name="statementDate"
                       rules={[{ required: true, message: 'Statement date is required.' }]}>
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Value date" name="valueDate">
              <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Description" name="description">
          <Input maxLength={2000} />
        </Form.Item>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item label="Reference no" name="referenceNo">
              <Input maxLength={120} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Cheque no" name="chequeNo">
              <Input maxLength={80} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item label="Debit amount (₹)" name="debitAmount">
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} precision={2} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Credit amount (₹)" name="creditAmount">
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} precision={2} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item label="Balance after (₹)" name="balanceAfter">
              <InputNumber style={{ width: '100%' }} step={0.01} precision={2} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Remarks" name="remarks">
          <Input.TextArea rows={2} maxLength={2000} />
        </Form.Item>
        <Alert type="info" showIcon
          message="Either debit OR credit must be > 0 (not both)."
          style={{ fontSize: 12 }} />
      </Form>
    </Modal>
  );
}

export default function BankReconciliationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { runWithApproval, ApprovalMfaModalSlot } = useApprovalMfa();

  const [header, setHeader] = useState(null);
  const [stmtLines, setStmtLines] = useState([]);
  const [bookEntries, setBookEntries] = useState([]);
  const [reportSummary, setReportSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [addLineOpen, setAddLineOpen] = useState(false);
  const [selectedStmtIds, setSelectedStmtIds]  = useState([]);
  const [selectedBookIds, setSelectedBookIds]  = useState([]);
  const [matching, setMatching] = useState(false);

  const refreshAll = useCallback(async () => {
    if (!id) return;
    setLoading(true); setLoadError(null);
    try {
      const [hdr, sl, be, rep] = await Promise.all([
        bankReconciliationApi.get(id),
        bankReconciliationApi.listStatementLines(id),
        bankReconciliationApi.listBookEntries(id, { includeMatched: 'true' }),
        bankReconciliationApi.report(id)
      ]);
      setHeader(hdr);
      setStmtLines(sl?.rows || []);
      setBookEntries(be?.rows || []);
      setReportSummary(rep || null);
    } catch (e) {
      setLoadError(e);
      message.error(friendlyAccountsErrorMessage(e, 'bank_reconciliation'));
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  const isEditable = header && (header.status === 'draft'
                              || header.status === 'in_progress');

  const onAddLine = useCallback(async (payload) => {
    await bankReconciliationApi.addStatementLine(id, payload);
    setAddLineOpen(false);
    message.success('Statement line added.');
    await refreshAll();
  }, [id, refreshAll]);

  const onDeleteLine = useCallback(async (line) => {
    Modal.confirm({
      title: 'Delete this statement line?',
      onOk: async () => {
        try {
          await bankReconciliationApi.deleteStatementLine(id, line.id);
          message.success('Statement line deleted.');
          await refreshAll();
        } catch (e) {
          message.error(friendlyAccountsErrorMessage(e, 'bank_reconciliation_lines'));
        }
      }
    });
  }, [id, refreshAll]);

  const onIgnoreLine = useCallback(async (line) => {
    try {
      await bankReconciliationApi.setStatementLineIgnore(id, line.id,
        line.matchStatus !== 'ignored');
      await refreshAll();
    } catch (e) {
      message.error(friendlyAccountsErrorMessage(e, 'bank_reconciliation_lines'));
    }
  }, [id, refreshAll]);

  const onMatch = useCallback(async () => {
    if (selectedStmtIds.length !== 1 || selectedBookIds.length !== 1) {
      message.warning('Select exactly one statement line AND one book entry to match.');
      return;
    }
    const stmt = stmtLines.find((s) => s.id === selectedStmtIds[0]);
    const book = bookEntries.find((b) => b.voucherLineId === selectedBookIds[0]);
    if (!stmt || !book) return;
    const stmtAmt = (Number(stmt.debitAmount) || 0) + (Number(stmt.creditAmount) || 0);
    const bookAmt = (Number(book.debitAmount) || 0) + (Number(book.creditAmount) || 0);
    if (Math.abs(stmtAmt - bookAmt) > 0.01) {
      Modal.confirm({
        title: 'Statement and book amounts differ',
        content: `Statement ₹${formatBRSAmount(stmtAmt)} vs book ₹${formatBRSAmount(bookAmt)}. Match anyway?`,
        okText: 'Match', cancelText: 'Cancel',
        onOk: () => doMatch(stmt, book, stmtAmt)
      });
    } else {
      await doMatch(stmt, book, stmtAmt);
    }
  }, [selectedStmtIds, selectedBookIds, stmtLines, bookEntries]);

  const doMatch = useCallback(async (stmt, book, matchedAmount) => {
    setMatching(true);
    try {
      await bankReconciliationApi.match(id, {
        statementLineId: stmt.id,
        voucherLineId:   book.voucherLineId,
        matchedAmount,
        matchType: 'manual'
      });
      message.success('Matched.');
      setSelectedStmtIds([]); setSelectedBookIds([]);
      await refreshAll();
    } catch (e) {
      message.error(friendlyAccountsErrorMessage(e, 'bank_reconciliation'));
    } finally { setMatching(false); }
  }, [id, refreshAll]);

  const onUnmatch = useCallback(async (book) => {
    if (!book.matchId) return;
    try {
      await bankReconciliationApi.unmatch(id, book.matchId);
      message.success('Unmatched.');
      await refreshAll();
    } catch (e) {
      message.error(friendlyAccountsErrorMessage(e, 'bank_reconciliation'));
    }
  }, [id, refreshAll]);

  const stmtColumns = useMemo(() => ([
    { title: 'Date', dataIndex: 'statementDate', key: 'sd', width: 100 },
    { title: 'Description', dataIndex: 'description', key: 'desc',
      ellipsis: true, render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Reference', dataIndex: 'referenceNo', key: 'ref', width: 110,
      ellipsis: true, render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Cheque #', dataIndex: 'chequeNo', key: 'chq', width: 95,
      render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Debit', dataIndex: 'debitAmount', key: 'dr', width: 100, align: 'right',
      render: (v) => v > 0 ? formatBRSAmount(v) : <Text type="secondary">—</Text> },
    { title: 'Credit', dataIndex: 'creditAmount', key: 'cr', width: 100, align: 'right',
      render: (v) => v > 0 ? formatBRSAmount(v) : <Text type="secondary">—</Text> },
    { title: 'Status', dataIndex: 'matchStatus', key: 'ms', width: 100,
      render: (v) => <Tag color={MATCH_STATUS_COLOR[v] || 'default'}>{MATCH_STATUS_LABEL[v] || v}</Tag> },
    { title: '', key: 'actions', width: 60, align: 'right',
      render: (_, r) => isEditable ? (
        <Space size={2}>
          <Tooltip title={r.matchStatus === 'ignored' ? 'Un-ignore' : 'Ignore'}>
            <Button size="small" type="text" icon={<FileSearchOutlined />}
              onClick={() => onIgnoreLine(r)} />
          </Tooltip>
          <Tooltip title="Delete"><Button size="small" type="text" danger
            icon={<DeleteOutlined />} disabled={r.matchStatus === 'matched'}
            onClick={() => onDeleteLine(r)} /></Tooltip>
        </Space>
      ) : null }
  ]), [isEditable, onDeleteLine, onIgnoreLine]);

  const bookColumns = useMemo(() => ([
    { title: 'Date', dataIndex: 'voucherDate', key: 'vd', width: 100 },
    { title: 'Voucher No', dataIndex: 'voucherNo', key: 'vn', width: 140,
      render: (v, r) => (
        <span>
          <code style={{ fontSize: 11 }}>{v}</code>
          <Text type="secondary" style={{ fontSize: 10, marginLeft: 6 }}>
            {r.voucherType}
          </Text>
        </span>
      )},
    { title: 'Narration / Ref', key: 'narr', ellipsis: true,
      render: (_, r) => r.narration || r.referenceNo || r.chequeNo
                       || <Text type="secondary">—</Text> },
    { title: 'Debit', dataIndex: 'debitAmount', key: 'dr', width: 100, align: 'right',
      render: (v) => v > 0 ? formatBRSAmount(v) : <Text type="secondary">—</Text> },
    { title: 'Credit', dataIndex: 'creditAmount', key: 'cr', width: 100, align: 'right',
      render: (v) => v > 0 ? formatBRSAmount(v) : <Text type="secondary">—</Text> },
    { title: 'Status', dataIndex: 'matchStatus', key: 'ms', width: 100,
      render: (v) => <Tag color={MATCH_STATUS_COLOR[v] || 'default'}>{MATCH_STATUS_LABEL[v] || v}</Tag> },
    { title: '', key: 'actions', width: 60, align: 'right',
      render: (_, r) => r.matchStatus === 'matched' && r.matchId && isEditable ? (
        <Tooltip title="Unmatch">
          <Button size="small" type="text" icon={<DisconnectOutlined />}
            onClick={() => onUnmatch(r)} />
        </Tooltip>
      ) : null }
  ]), [isEditable, onUnmatch]);

  const summary = reportSummary?.summary || {};

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', marginBottom: 4 }}>
          <Space>
            <Button size="small" icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/erp/accounts/bank-reconciliations')}>
              Back
            </Button>
            <Title level={4} style={{ margin: 0 }}>Bank Reconciliation</Title>
            {header ? (
              <Tag color={RECONCILIATION_STATUS_COLOR[header.status]}>
                {RECONCILIATION_STATUS_LABEL[header.status]}
              </Tag>
            ) : null}
          </Space>
          <Space>
            <Button size="small" icon={<ReloadOutlined />}
              onClick={refreshAll} loading={loading}>Refresh</Button>
            <Button size="small" type="primary" icon={<PlusOutlined />}
              disabled={!isEditable} onClick={() => setAddLineOpen(true)}>
              Add Statement Line
            </Button>
          </Space>
        </div>

        {loadError ? (
          <Alert type="error" showIcon style={{ marginBottom: 10 }}
            message="Could not load reconciliation"
            description={friendlyAccountsErrorMessage(loadError, 'bank_reconciliation')}
            action={<Button size="small" onClick={refreshAll}>Retry</Button>} />
        ) : null}

        {header ? (
          <Card size="small" style={{ marginBottom: 10 }}>
            <Descriptions size="small" column={4} bordered>
              <Descriptions.Item label="Bank Account">
                {header.bankAccountCode ? <code>{header.bankAccountCode}</code> : null}
                {' '}{header.bankAccountName}
              </Descriptions.Item>
              <Descriptions.Item label="Statement Period">
                {header.statementFromDate} → {header.statementToDate}
              </Descriptions.Item>
              <Descriptions.Item label="Book Closing">
                ₹ {formatBRSAmount(header.bookClosingBalance)}
              </Descriptions.Item>
              <Descriptions.Item label="Statement Closing">
                {header.statementClosingBalance == null
                  ? <Text type="secondary">not set</Text>
                  : `₹ ${formatBRSAmount(header.statementClosingBalance)}`}
              </Descriptions.Item>
            </Descriptions>

            <Row gutter={[10, 10]} style={{ marginTop: 12 }}>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic title="Difference (₹)"
                    value={Math.abs(Number(header.differenceAmount) || 0)}
                    precision={2}
                    valueStyle={{ color: Math.abs(Number(header.differenceAmount) || 0) < 0.01
                                          ? '#1f7a25' : '#b91c1c' }} />
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic title="Matched amount (₹)"
                    value={summary.totalMatchedAmount || 0} precision={2} />
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic title="Matched lines"
                    value={summary.matchedCount || 0} />
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Statistic title="Unmatched lines"
                    value={summary.unmatchedStatementCount || 0}
                    valueStyle={{ color: '#b65b00' }} />
                </Card>
              </Col>
            </Row>
          </Card>
        ) : null}

        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Card size="small"
                  title={<Text strong>Bank statement lines</Text>}
                  extra={<Text type="secondary" style={{ fontSize: 11 }}>
                    {stmtLines.length} line(s)
                  </Text>}>
              <Table
                size="small"
                rowKey="id"
                loading={loading}
                columns={stmtColumns}
                dataSource={stmtLines}
                rowSelection={isEditable ? {
                  type: 'radio',
                  selectedRowKeys: selectedStmtIds,
                  onChange: setSelectedStmtIds,
                  getCheckboxProps: (r) => ({ disabled: r.matchStatus === 'matched'
                                                     || r.matchStatus === 'ignored' })
                } : undefined}
                pagination={{ pageSize: 20, size: 'small' }}
                locale={{ emptyText:
                  <Empty description={friendlyAccountsEmptyMessage('bank_reconciliation_lines')} /> }}
              />
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card size="small"
                  title={<Text strong>Book entries (bank ledger)</Text>}
                  extra={
                    <Space>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {bookEntries.length} line(s)
                      </Text>
                      <Button size="small" type="primary" icon={<LinkOutlined />}
                        disabled={!isEditable
                                  || selectedStmtIds.length !== 1
                                  || selectedBookIds.length !== 1
                                  || matching}
                        loading={matching}
                        onClick={onMatch}>
                        Match
                      </Button>
                    </Space>
                  }>
              <Table
                size="small"
                rowKey="voucherLineId"
                loading={loading}
                columns={bookColumns}
                dataSource={bookEntries}
                rowSelection={isEditable ? {
                  type: 'radio',
                  selectedRowKeys: selectedBookIds,
                  onChange: setSelectedBookIds,
                  getCheckboxProps: (r) => ({ disabled: r.matchStatus === 'matched' })
                } : undefined}
                pagination={{ pageSize: 20, size: 'small' }}
                locale={{ emptyText:
                  <Empty description={friendlyAccountsEmptyMessage('bank_reconciliation_book')} /> }}
              />
            </Card>
          </Col>
        </Row>

        <AddStatementLineModal
          open={addLineOpen}
          onClose={() => setAddLineOpen(false)}
          onSaved={onAddLine} />

        {ApprovalMfaModalSlot}
      </div>
    </ErpMasterShell>
  );
}
